import { h as __h } from "preact";
import { Component } from "preact";
import { machineAccessInviteResponse, machineAccessResponse } from "~/backend/validate.js";
import { ApiError, authedApiRequest, query, response } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import { array, object, parse, string } from "~/util/validate.js";
import Avatar from "~/component/Avatar.js";
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";
import Icon from "~/component/Icon.js";
import Modal from "~/component/Modal.js";
import Time from "~/component/Time.js";
import input from "~/style/input.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./ShareModal.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./ShareModal.module.css", import.meta.url);
const css = adopt(sheet);
class InviteError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        conflict: "That person already has access or a pending invite.",
        not_found: "Couldn't find this computer.",
        forbidden: "Computers may not be shared with enterprise accounts.",
        domain_claimed: "Computers may not be shared with enterprise accounts."
      }
    };
  }
}

/**
 * @typedef {object} Props
 * @property {(onRefresh: () => void, signal: AbortSignal) => void} subscribe
 * @property {string} id
 * @property {string} name
 * @property {boolean} open
 * @property {() => void} onClose
 */

/** @extends {Component<Props>} */
export default class ShareModal extends Component {
  unlisten = new AbortController();

  /** @override */
  componentDidMount() {
    const invalidate = () => Query.invalidate([`/v2/machines/${this.props.id}/access-invites`], [`/v2/machines/${this.props.id}/accesses`]);
    this.props.subscribe(invalidate, this.unlisten.signal);
  }

  /** @override */
  componentWillUnmount() {
    this.unlisten.abort();
  }
  accesses = query(() => ({
    key: [`/v2/machines/${this.props.id}/accesses`],
    enabled: this.props.open
  }), {
    schema: response(array(machineAccessResponse))
  });
  invites = query(() => ({
    key: [`/v2/machines/${this.props.id}/access-invites`],
    enabled: this.props.open
  }), {
    schema: response(array(machineAccessInviteResponse))
  });
  invite = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */async ev => {
    const form = ev.currentTarget;
    const formdata = new FormData(form);
    const {
      email
    } = parse(object({
      email: string
    }), Object.fromEntries(formdata.entries()));
    await authedApiRequest("POST", `/v2/machines/${this.props.id}/access-invites`, {
      json: {
        email
      },
      schema: response(machineAccessInviteResponse)
    });
    Query.invalidate([`/v2/machines/${this.props.id}/access-invites`]);
    form.reset();
  });

  /** @param {machineAccessInviteResponse} invite */
  async resendInvite(invite) {
    try {
      await authedApiRequest("POST", `/v2/access-invites/${invite.id}/resend`);
      alert(`Invite resent to ${invite.receiver.email}!`, {
        type: "success"
      });
    } catch {
      alert(`Couldn't resend the invite to ${invite.receiver.email}.`, {
        type: "error"
      });
    }
    // refetch (rather than invalidate) so callers stay pending until the fresh list lands
    await this.invites.fetch().catch(() => {});
  }

  /** @param {machineAccessInviteResponse} invite */
  async withdrawInvite(invite) {
    try {
      await authedApiRequest("DELETE", `/v2/access-invites/${invite.id}`);
    } catch {
      alert(`Couldn't withdraw the invite to ${invite.receiver.email}.`, {
        type: "error"
      });
    }
    await this.invites.fetch().catch(() => {});
  }

  /** @param {machineAccessResponse} access */
  async removeAccess(access) {
    try {
      await authedApiRequest("DELETE", `/v2/machines/${this.props.id}/accesses/${access.accessor_id}`);
    } catch {
      alert(`Couldn't remove ${access.accessor.name}.`, {
        type: "error"
      });
    }
    await this.accesses.fetch().catch(() => {});
  }
  render() {
    const {
      name,
      open,
      onClose
    } = this.props;
    const invites = this.invites.data?.data ?? [];
    const accesses = this.accesses.data?.data ?? [];
    const loading = !this.invites.initialized || !this.accesses.initialized;
    const listError = this.invites.error ?? this.accesses.error;
    const inviteError = this.invite.error && InviteError.from(this.invite.error);
    return __h(Modal, {
      title: "Share " + name,
      open: open,
      onClose: onClose
    }, __h("label", {
      "for": "share_email"
    }, "Enter someone's email to share ", name, " with them."), __h("form", {
      "class": css.invite,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        ev.preventDefault();
        this.invite.invoke(ev);
      },
      onReset: () => this.invite.reset()
    }, __h("input", {
      "class": input.input + " " + css.email,
      type: "email",
      name: "email",
      id: "share_email",
      placeholder: "alice@phaze.app",
      required: true
    }), __h(Button, {
      type: "submit",
      loading: this.invite.loading
    }, "Invite")), __h("p", {
      "class": css.error
    }, inviteError?.message), __h(Data, {
      loading: loading,
      error: listError && __h("p", {
        "class": css.error
      }, listError.message),
      empty: !invites.length && !accesses.length && __h("p", {
        "class": css.empty
      }, "You haven't shared ", name, " with anyone yet.")
    }, __h("ul", {
      "class": css.list
    }, invites.map(invite => __h(InviteRow, {
      key: invite.id,
      invite: invite,
      onResend: () => this.resendInvite(invite),
      onWithdraw: () => this.withdrawInvite(invite)
    })), accesses.map(access => __h(AccessRow, {
      key: access.id,
      access: access,
      onRemove: () => this.removeAccess(access)
    })))), __h("form", {
      "class": css.actions,
      method: "dialog"
    }, __h(Button, {
      type: "submit",
      level: "primary"
    }, "Close")));
  }
}

/**
 * @typedef {object} InviteRowProps
 * @property {machineAccessInviteResponse} invite
 * @property {() => Promise<void>} onResend
 * @property {() => Promise<void>} onWithdraw
 */

/** @extends {Component<InviteRowProps>} */
class InviteRow extends Component {
  resend = new PromiseSignal(() => this.props.onResend());
  withdraw = new PromiseSignal(() => this.props.onWithdraw());
  render() {
    const {
      invite
    } = this.props;
    return __h("li", {
      "class": css.row
    }, __h(Icon, {
      name: "email",
      size: 16
    }), __h("div", {
      "class": css.info
    }, __h("span", {}, invite.receiver.email), __h("span", {
      "class": css.meta
    }, invite.is_expired ? "Expired" : ["Expires ", __h(Time, {
      datetime: new Date(invite.expires_at)
    })])), __h("div", {
      "class": css.buttons
    }, invite.is_expired && __h(Button, {
      size: "small",
      loading: this.resend.loading,
      onClick: () => this.resend.invoke()
    }, "Resend"), __h(Button, {
      size: "small",
      kind: "negative",
      loading: this.withdraw.loading,
      onClick: () => this.withdraw.invoke()
    }, "Withdraw")));
  }
}

/**
 * @typedef {object} AccessRowProps
 * @property {machineAccessResponse} access
 * @property {() => Promise<void>} onRemove
 */

/** @extends {Component<AccessRowProps>} */
class AccessRow extends Component {
  remove = new PromiseSignal(() => this.props.onRemove());
  render() {
    const {
      access
    } = this.props;
    return __h("li", {
      "class": css.row
    }, __h(Avatar, {
      name: access.accessor.name,
      size: 24
    }), __h("div", {
      "class": css.info
    }, __h("span", {}, access.accessor.name), __h("span", {
      "class": css.meta
    }, access.accessor.email)), __h("div", {
      "class": css.buttons
    }, __h(Button, {
      size: "small",
      kind: "negative",
      loading: this.remove.loading,
      onClick: () => this.remove.invoke()
    }, "Remove")));
  }
}